import { type IntlShape } from 'react-intl';
import { type BasicPerson, type Person, UserGeneratedInputType } from 'types';
import { type ApiGroup } from 'views/Widgets/Filters/AdvancedSpecializedFilter/types';
import TimezoneSelector from 'views/Widgets/Inputs/TimezoneSelector';
import {
  INPUT_TYPES,
  USER_GENERATED_INPUT_TYPE_OPTIONS,
} from 'views/Widgets/Inputs/ValidatedInputTypes';

// ── Survey types & interfaces ────────────────────────────────────────

export type CustomSurveyStatus =
  | 'DRAFT'
  | 'LIVE'
  | 'PAUSED'
  | 'ARCHIVED'
  | 'DELETED';

export interface SurveyQuestionAssociation {
  through_id?: number;
  question_uuid: string;
  order: number;
  required: boolean;
  target_filter: Record<string, unknown>;
  // Denormalized from the latest version of the question (read-only, from API)
  question?: Record<string, unknown>;
  question_type?: string;
  question_label?: string;
  question_data?: Record<string, unknown>;
  // Local-only, never sent to the backend (CustomSurveyBasicsTab builds
  // its save payload from an explicit field list, not a spread of this
  // object). Set from the last failed save's per-row error (see handleSave)
  // and travels with this row through reordering/removal for free, since
  // it's just a field on the row object rather than a separate lookup keyed
  // by something that would need to be kept in sync as rows move.
  filterError?: string;
}

export interface CustomSurveyData {
  id?: number;
  survey_type: string;
  cadence?: string | null;
  milestone_days_offset?: number | null;
  milestone_field?: string | null;
  scheduled_time?: string | null;
  // The API shape: a bare IANA name. The editors' form objects hold a whole
  // TimezoneOption here instead (that's what SelectInput round-trips), and
  // cast at the seed site - the same trick scheduled_time above already uses
  // to carry a Date through a string-typed field.
  scheduled_timezone?: string | null;
  target_filter?: Record<string, unknown> | null;
  admin_target_audience?: Record<string, unknown> | null;
  days_to_respond?: number | null;
  owner?: Person;
  administrators?: Person[];
  administrator_ids?: number[];
  name: string;
  description?: string;
  status: CustomSurveyStatus;
  questions_at_latest_version?: SurveyQuestionAssociation[];
  // PER_PERSON surveys only: the people queued to receive one, each with their
  // own send time. Written back in the same payload, so saving the survey is
  // what commits queue changes.
  scheduled_participants?: ScheduledParticipant[];
  instance_count?: number;
  has_responses?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ScheduledParticipant {
  id?: number;
  // Read as a nested person, written as a bare id.
  person?: Person;
  person_id: number;
  scheduled_time: string;
  scheduled_timezone: string;
  // Set once the survey has actually gone out. A sent row is history: it can
  // no longer be retimed or removed, and the backend ignores any attempt to.
  sent_at?: string | null;
}

export const NAME_DESCRIPTION_INPUTS = (
  formatMessage: IntlShape['formatMessage'],
  isSaving: boolean = false
) => [
  {
    name: 'name',
    type: INPUT_TYPES.TEXT,
    label: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.editor.name_label',
      defaultMessage: 'Name',
    }),
    placeholder: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.editor.name_placeholder',
      defaultMessage: 'Enter survey name',
    }),
    required: true,
    jsonPath: '$.name',
    disabled: isSaving,
  },
  {
    name: 'description',
    type: INPUT_TYPES.TEXTAREA,
    label: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.editor.description_label',
      defaultMessage: 'Description',
    }),
    placeholder: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.editor.description_placeholder',
      defaultMessage: 'Optional description',
    }),
    rows: 3,
    jsonPath: '$.description',
    disabled: isSaving,
  },
];

export const SURVEY_TYPES = (formatMessage: IntlShape['formatMessage']) => [
  {
    id: 'ONE_TIME',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.type.one_time',
      defaultMessage: 'One time for a list of people',
    }),
  },
  {
    id: 'MILESTONE',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.type.milestone',
      defaultMessage: "Days after a person's milestone date",
    }),
  },
  {
    id: 'PERIODIC',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.type.periodic',
      defaultMessage: 'Periodically (multiple times per person)',
    }),
  },
  {
    id: 'PER_PERSON',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.type.per_person',
      defaultMessage: 'Scheduled per recipient',
    }),
  },
];

export const SURVEY_STATUSES = (formatMessage: IntlShape['formatMessage']) => [
  {
    id: 'DRAFT',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.status.draft',
      defaultMessage: 'Draft',
    }),
  },
  {
    id: 'LIVE',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.status.live',
      defaultMessage: 'Live',
    }),
  },
  {
    id: 'PAUSED',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.status.paused',
      defaultMessage: 'Paused',
    }),
  },
  {
    id: 'ARCHIVED',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.status.archived',
      defaultMessage: 'Archived',
    }),
  },
];

export const MAX_DAYS_TO_RESPOND_FOR_CADENCE: Record<string, number> = {
  WEEKLY: 7,
  FORTNIGHTLY: 14,
  MONTHLY: 30,
};

export const isDaysToRespondValid = (obj: Record<string, unknown>): boolean => {
  const daysToRespondNumber = Number(obj.days_to_respond);
  return (
    obj.days_to_respond != null &&
    obj.days_to_respond !== '' &&
    Number.isInteger(daysToRespondNumber) &&
    daysToRespondNumber >= 1
  );
};

const getDaysToRespondValidationError = (
  obj: Record<string, unknown>,
  formatMessage: IntlShape['formatMessage']
): string | undefined => {
  if (!isDaysToRespondValid(obj)) {
    return formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.editor.validation.days_to_respond_invalid',
      defaultMessage: 'Enter a whole number of at least 1.',
    });
  }
  if (obj.survey_type === 'PERIODIC' && obj.cadence) {
    const maxDaysToRespond =
      MAX_DAYS_TO_RESPOND_FOR_CADENCE[obj.cadence as string];
    if (
      maxDaysToRespond != null &&
      Number(obj.days_to_respond) >= maxDaysToRespond
    ) {
      return formatMessage(
        {
          id: 'app.views.administration.custom_surveys.surveys.editor.validation.days_to_respond_exceeds_cadence',
          defaultMessage:
            'Days to respond must be less than the cadence ({maxDaysToRespond} days) to avoid overlapping survey instances.',
        },
        { maxDaysToRespond }
      );
    }
  }
  return undefined;
};

// Shared between the create/edit modal and the full-page editor's settings
// form so both surfaces validate identically.
export const validateCustomSurveySettings = (
  obj: Record<string, unknown>,
  formatMessage: IntlShape['formatMessage']
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!(obj.name as string)?.trim()) {
    errors.name = formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.editor.validation.name_required',
      defaultMessage: 'Name is required.',
    });
  }
  if (!obj.survey_type) {
    errors.survey_type = formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.editor.validation.type_required',
      defaultMessage: 'Survey type is required.',
    });
  }
  if (obj.survey_type === 'PERIODIC' && !obj.cadence) {
    errors.cadence = formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.editor.validation.cadence_required',
      defaultMessage: 'Cadence is required.',
    });
  }
  // Only the types that carry a survey-level schedule. Listed by name rather
  // than as "not MILESTONE": PER_PERSON has no schedule of its own either -
  // each person on its queue carries their own send time - and the backend's
  // type-consistency constraint requires both columns to be null for it.
  const hasOwnSchedule =
    obj.survey_type === 'ONE_TIME' || obj.survey_type === 'PERIODIC';
  if (hasOwnSchedule && !obj.scheduled_time) {
    errors.scheduled_time = formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.editor.validation.scheduled_time_required',
      defaultMessage: 'Scheduled time is required.',
    });
  }
  if (hasOwnSchedule && !obj.scheduled_timezone) {
    errors.scheduled_timezone = formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.editor.validation.scheduled_timezone_required',
      defaultMessage: 'Timezone is required.',
    });
  }
  const daysToRespondError = getDaysToRespondValidationError(
    obj,
    formatMessage
  );
  if (daysToRespondError) {
    errors.days_to_respond = daysToRespondError;
  }
  if (obj.survey_type === 'MILESTONE') {
    if (!obj.milestone_field) {
      errors.milestone_field = formatMessage({
        id: 'app.views.administration.custom_surveys.surveys.editor.validation.milestone_field_required',
        defaultMessage: 'Milestone date is required.',
      });
    }
    if (obj.milestone_days_offset == null || obj.milestone_days_offset === '') {
      errors.milestone_days_offset = formatMessage({
        id: 'app.views.administration.custom_surveys.surveys.editor.validation.milestone_days_required',
        defaultMessage: 'Days offset is required.',
      });
    }
  }
  return errors;
};

// The scheduled_time + scheduled_timezone pair, shared by ONE_TIME and
// PERIODIC on both the create modal and the full-page editor. Both types let
// whoever sets the schedule pick a local wall clock and the zone to read it
// in; only the date field's label differs, since for PERIODIC the answer is
// the *first* send.
export const SCHEDULE_INPUTS = (
  formatMessage: IntlShape['formatMessage'],
  {
    dateLabel,
    value,
    // Deliberately undefaulted: the full-page editor passes undefined for a
    // survey that already has a scheduled_time, so an existing date in the
    // past isn't retroactively rejected by the picker. A default of 'today'
    // here would silently undo that.
    minDate,
    disabled = false,
    hideClearButton = false,
  }: {
    dateLabel: string;
    value?: string | null;
    minDate?: string;
    disabled?: boolean;
    hideClearButton?: boolean;
  }
) => [
  {
    name: 'scheduled_time',
    type: INPUT_TYPES.DATE_PICKER,
    enableTime: true,
    label: dateLabel,
    placeholder: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.editor.scheduled_time_placeholder',
      defaultMessage: 'Select date and time',
    }),
    value,
    minDate,
    required: true,
    disabled,
    hideClearButton,
    autoFocus: false,
  },
  {
    name: 'scheduled_timezone',
    type: INPUT_TYPES.CUSTOM_COMPONENT,
    component: TimezoneSelector,
    label: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.editor.scheduled_timezone_label',
      defaultMessage: 'Which timezone is that time in?',
    }),
    required: true,
    disabled,
    autoFocus: false,
  },
];

export const CADENCE_OPTIONS = (formatMessage: IntlShape['formatMessage']) => [
  {
    id: 'WEEKLY',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.cadence.weekly',
      defaultMessage: 'Weekly',
    }),
  },
  {
    id: 'FORTNIGHTLY',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.cadence.fortnightly',
      defaultMessage: 'Fortnightly',
    }),
  },
  {
    id: 'MONTHLY',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.cadence.monthly',
      defaultMessage: 'Monthly',
    }),
  },
];

export const MILESTONE_FIELD_OPTIONS = (
  formatMessage: IntlShape['formatMessage']
) => [
  {
    id: 'original_hire_date',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.milestone_field.original_hire_date',
      defaultMessage: 'Original hire date',
    }),
  },
  {
    id: 'latest_hire_date',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.milestone_field.latest_hire_date',
      defaultMessage: 'Latest hire date',
    }),
  },
  {
    id: 'position_start_date',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.milestone_field.position_start_date',
      defaultMessage: 'Position start date',
    }),
  },
  {
    id: 'position_effective_date',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.surveys.milestone_field.position_effective_date',
      defaultMessage: 'Position effective date',
    }),
  },
];

// ── Question types & interfaces ──────────────────────────────────────

export type CustomSurveyQuestionStatus = 'ACTIVE' | 'ARCHIVED' | 'DELETED';

export interface CustomSurveyQuestionData {
  id?: number;
  uuid?: string;
  version?: number;
  question: UserGeneratedInputType;
  status: CustomSurveyQuestionStatus;
  has_answers?: boolean;
  organization?: number;
  owner?: Person;
  administrators?: Person[];
  administrator_ids?: number[];
  created_at?: string;
  updated_at?: string;
}

export const ALLOWED_QUESTION_TYPES = [
  INPUT_TYPES.STAR_RATING,
  INPUT_TYPES.TEXTAREA,
  INPUT_TYPES.RICH_TEXT_EDITOR,
  INPUT_TYPES.SWITCH,
  INPUT_TYPES.LIKERT,
  INPUT_TYPES.MULTIPLE_CHOICE,
  INPUT_TYPES.DROPDOWN,
  INPUT_TYPES.DATE_PICKER,
];

const ALLOWED_TYPES_SET = new Set(ALLOWED_QUESTION_TYPES);

export const QUESTION_TYPES = (formatMessage: IntlShape['formatMessage']) =>
  USER_GENERATED_INPUT_TYPE_OPTIONS(formatMessage, true).filter((opt) =>
    ALLOWED_TYPES_SET.has(opt.id as string)
  );

export const getQuestionTypeName = (
  type: string | undefined,
  formatMessage: IntlShape['formatMessage']
): string => {
  if (!type) return '';
  const normalized = type.toUpperCase();
  const opt = QUESTION_TYPES(formatMessage).find(
    (q) => String(q.id).toUpperCase() === normalized
  );
  return opt?.name ?? type;
};

export const QUESTION_STATUSES = (
  formatMessage: IntlShape['formatMessage']
) => [
  {
    id: 'ACTIVE',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.questions.status.active',
      defaultMessage: 'Active',
    }),
  },
  {
    id: 'ARCHIVED',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.questions.status.archived',
      defaultMessage: 'Archived',
    }),
  },
];

// These are QuestionType.name values from QuestionEditor's internal questionTypes
// array (NOT INPUT_TYPES values). Excluding all 5 special types leaves only
// "Custom question", which QuestionEditor auto-selects, skipping the type
// selection screen and landing the user directly on the custom question editor.
export const EXCLUDE_SPECIAL_QUESTIONS = [
  'positive_skills',
  'positive_comments',
  'negative_skills',
  'negative_comments',
  'rating',
];

// ── Responses page ──────────────────────────────────────────────────────

// Tabs of the survey responses page. Mirrored in the URL as `?tab=` so a tab
// is linkable.
export type SurveyResponsesTab = 'participants' | 'questions';

export const SURVEY_RESPONSES_TAB_PARAM = 'tab';
export const SURVEY_RESPONSES_DEFAULT_TAB: SurveyResponsesTab = 'participants';
export const SURVEY_RESPONSES_TABS: SurveyResponsesTab[] = [
  'participants',
  'questions',
];

// Tabs of the survey editor page, in the URL as `?tab=` the same way.
export type SurveyEditorTab = 'basics' | 'reports';

export const SURVEY_EDITOR_TAB_PARAM = 'tab';
export const SURVEY_EDITOR_DEFAULT_TAB: SurveyEditorTab = 'basics';
export const SURVEY_EDITOR_TABS: SurveyEditorTab[] = ['basics', 'reports'];

// ── Reports ───────────────────────────────────────────────────────────

// Mirrors api.models.CustomSurveyReport.SplitType.
export type ReportSplitType = 'DEMOGRAPHIC' | 'LEADERS' | 'CUSTOM';

export interface ReportSplitOption {
  key: string;
  name: string;
}

// Mirrors api.logic.custom_survey_report_slices.SplitOptions, returned by
// GET custom-survey/surveys/{id}/report-split-options.
export interface ReportSplitOptions {
  demographic: ReportSplitOption[];
  leader_levels: ReportSplitOption[];
  // The survey's own ceiling (admin_target_audience AND target_filter), or
  // null when the survey has no restriction. Shown locked/read-only for the
  // CUSTOM split - display only, the server ANDs whatever's submitted
  // against this same ceiling regardless (see combine_filters_and).
  effective_target_filter: ApiGroup | null;
}

// One audience slice, as returned by CustomSurveyReportSerializer's
// lightweight `items` field - just enough to render the report editor's
// audience table and derive a status badge. Editing a slice's sharing
// (`viewers`/`published`) goes through CustomSurveyReportItemViewSet
// instead - see custom-survey/report-items/{id}.
export interface CustomSurveyReportItemData {
  id: number;
  name: string;
  key: string;
  order: number;
  published: boolean;
  viewers: BasicPerson[];
}

// Returned by GET custom-survey/report-items/{id} - the standalone/shared
// item view's single request for everything it needs to render: the item
// itself, plus every chart on its report already computed against this
// item's own target_audience (see SurveyReportChart below).
export interface CustomSurveyReportItemDetailData
  extends CustomSurveyReportItemData {
  report_name: string;
  charts: SurveyReportChart[];
}

// A report item shared with the viewer, as returned by
// custom-survey/report-items/shared-with-me - just enough to label a row
// (survey/report/audience) and link to the standalone item view. Flat by
// design: the viewer isn't shown anyone else's viewers, and doesn't need the
// full CustomSurveyReportItemData shape for a list they can only read.
export interface SharedCustomSurveyReportItemData {
  id: number;
  name: string;
  report_id: number;
  report_name: string;
  survey_id: number;
  survey_name: string;
}

// Mirrors CustomSurveyReportSerializer.
export interface CustomSurveyReportData {
  id: number;
  survey: number;
  name: string;
  order: number;
  split_type: ReportSplitType;
  split_key: string;
  owner?: Person;
  items: CustomSurveyReportItemData[];
  // How many charts this report has - just a count, for the reports list
  // column (mirrors how `items` above is used to derive audience count).
  chart_count: number;
  created_at?: string;
  updated_at?: string;
}

// Not a stored field - CustomSurveyReport has no status column of its own,
// so this is aggregated from the items' `published` flag (same situation
// CampaignReportSerializer solves server-side for CampaignReportItem
// statuses). Kept as a stable, untranslated identifier so it can be
// compared/styled independent of the current locale's text.
export type CustomSurveyReportStatus =
  | 'UNPUBLISHED'
  | 'PARTIALLY_PUBLISHED'
  | 'PUBLISHED';

export const getCustomSurveyReportStatus = (
  report: Pick<CustomSurveyReportData, 'items'>
): CustomSurveyReportStatus => {
  const audienceCount = report.items.length;
  const publishedCount = report.items.filter((item) => item.published).length;
  if (audienceCount === 0 || publishedCount === 0) {
    return 'UNPUBLISHED';
  }
  if (publishedCount === audienceCount) {
    return 'PUBLISHED';
  }
  return 'PARTIALLY_PUBLISHED';
};

// Translated label for an already-derived status - mirrors
// formatCampaignReportStatus's switch-on-status shape (EngagementSurvey.tsx).
export const formatCustomSurveyReportStatus = (
  status: CustomSurveyReportStatus,
  formatMessage: IntlShape['formatMessage']
): string => {
  switch (status) {
    case 'UNPUBLISHED':
      return formatMessage({
        id: 'app.views.administration.custom_surveys.reports.status.unpublished',
        defaultMessage: 'Unpublished',
      });
    case 'PARTIALLY_PUBLISHED':
      return formatMessage({
        id: 'app.views.administration.custom_surveys.reports.status.partial',
        defaultMessage: 'Partially published',
      });
    case 'PUBLISHED':
      return formatMessage({
        id: 'app.views.administration.custom_surveys.reports.status.published',
        defaultMessage: 'Published',
      });
  }
};

export const REPORT_SPLIT_TYPE_OPTIONS = (
  formatMessage: IntlShape['formatMessage']
): { id: ReportSplitType; name: string; helperText: string }[] => [
  {
    id: 'DEMOGRAPHIC',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.reports.split.demographic.name',
      defaultMessage: 'By demographic',
    }),
    helperText: formatMessage({
      id: 'app.views.administration.custom_surveys.reports.split.demographic.helper',
      defaultMessage:
        'Create one report per value of a dimension (department, location, …).',
    }),
  },
  {
    id: 'LEADERS',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.reports.split.leaders.name',
      defaultMessage: 'By leader',
    }),
    helperText: formatMessage({
      id: 'app.views.administration.custom_surveys.reports.split.leaders.helper',
      defaultMessage:
        'Create one report per leader at a chosen level of the org.',
    }),
  },
  {
    id: 'CUSTOM',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.reports.split.custom.name',
      defaultMessage: 'Custom',
    }),
    helperText: formatMessage({
      id: 'app.views.administration.custom_surveys.reports.split.custom.helper',
      defaultMessage:
        'Create a single report with a hand-built audience. Leave empty for everyone.',
    }),
  },
];

export const validateCustomSurveyReport = (
  obj: Record<string, unknown>,
  formatMessage: IntlShape['formatMessage']
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!(obj.name as string)?.trim()) {
    errors.name = formatMessage({
      id: 'app.views.administration.custom_surveys.reports.create_modal.validation.name_required',
      defaultMessage: 'Report name is required.',
    });
  }
  if (!obj.split_key && obj.split_type !== 'CUSTOM') {
    errors.split_key =
      obj.split_type === 'LEADERS'
        ? formatMessage({
            id: 'app.views.administration.custom_surveys.reports.create_modal.validation.leader_level_required',
            defaultMessage: 'Leader level is required.',
          })
        : formatMessage({
            id: 'app.views.administration.custom_surveys.reports.create_modal.validation.dimension_required',
            defaultMessage: 'Dimension is required.',
          });
  }
  return errors;
};

// ── Charts ────────────────────────────────────────────────────────────

// Mirrors api.models.CustomSurveyChartConfig.Width.
export type ChartWidth = 'FULL' | 'HALF' | 'THIRD';

export const CHART_WIDTH_TO_COL: Record<ChartWidth, number> = {
  FULL: 12,
  HALF: 6,
  THIRD: 4,
};

export const CHART_WIDTH_OPTIONS = (
  formatMessage: IntlShape['formatMessage']
): { id: ChartWidth; name: string }[] => [
  {
    id: 'FULL',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.charts.width.full',
      defaultMessage: 'Full row',
    }),
  },
  {
    id: 'HALF',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.charts.width.half',
      defaultMessage: 'Half',
    }),
  },
  {
    id: 'THIRD',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.charts.width.third',
      defaultMessage: 'Third',
    }),
  },
];

// Mirrors api.logic.custom_survey_analytics's GROUP_BY_* / chart-type
// vocabulary. Distribution types render one bar/doughnut/etc; LINE and
// RESPONSES aren't "distribution" types - see SurveyChartView.
export type ChartType =
  | 'DOUGHNUT'
  | 'BAR'
  | 'HORIZONTAL_BAR'
  | 'LINE'
  | 'RESPONSES';
export type ChartGroupBy = 'single' | 'by_instance' | 'by_period';
export type ChartPeriodGranularity = 'month' | 'quarter';

export const CHART_TYPE_OPTIONS = (
  formatMessage: IntlShape['formatMessage']
): { id: ChartType; name: string }[] => [
  {
    id: 'DOUGHNUT',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.charts.type.doughnut',
      defaultMessage: 'Doughnut',
    }),
  },
  {
    id: 'BAR',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.charts.type.bar',
      defaultMessage: 'Bar',
    }),
  },
  {
    id: 'HORIZONTAL_BAR',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.charts.type.horizontal_bar',
      defaultMessage: 'Stacked bar',
    }),
  },
  {
    id: 'LINE',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.charts.type.line',
      defaultMessage: 'Line',
    }),
  },
  {
    id: 'RESPONSES',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.charts.type.responses',
      defaultMessage: 'Responses (table)',
    }),
  },
];

export const CHART_GROUP_BY_OPTIONS = (
  formatMessage: IntlShape['formatMessage']
): { id: ChartGroupBy; name: string }[] => [
  {
    id: 'single',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.charts.group_by.single',
      defaultMessage: 'Overall (all responses combined)',
    }),
  },
  {
    id: 'by_instance',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.charts.group_by.by_instance',
      defaultMessage: 'By instance',
    }),
  },
  {
    id: 'by_period',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.charts.group_by.by_period',
      defaultMessage: 'By period',
    }),
  },
];

export const CHART_PERIOD_OPTIONS = (
  formatMessage: IntlShape['formatMessage']
): { id: ChartPeriodGranularity; name: string }[] => [
  {
    id: 'month',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.charts.period.month',
      defaultMessage: 'Month',
    }),
  },
  {
    id: 'quarter',
    name: formatMessage({
      id: 'app.views.administration.custom_surveys.charts.period.quarter',
      defaultMessage: 'Quarter',
    }),
  },
];

// The chart's definition, both as the persisted `config` JSON (camelCase,
// matching CustomSurveyChartConfig.config's own doc comment) and as the
// chart-builder modal's working object.
export interface SurveyChartConfigBody {
  questionUuid: string;
  chartType: ChartType;
  groupBy: ChartGroupBy;
  periodGranularity?: ChartPeriodGranularity;
  // Which statement of a multi-statement LIKERT a trend plots.
  statement?: string;
}

// One chart saved on a report - mirrors CustomSurveyChartConfigSerializer.
export interface SurveyChartConfig {
  id: number;
  report: number;
  owner?: Person;
  name: string;
  order: number;
  width: ChartWidth;
  config: SurveyChartConfigBody;
  created_at?: string;
  updated_at?: string;
}

// {uuid, type, label, retired, statements} - mirrors
// all_survey_questions / filter_chartable_questions in
// api.logic.custom_survey_analytics.
export interface ChartableQuestion {
  uuid: string;
  type: string;
  label: string;
  // Asked by past instances, but no longer on the survey.
  retired: boolean;
  // A multi-statement LIKERT's statements; empty for every other question.
  statements: { name: string; label: string }[];
}

// Returned by GET custom-survey/surveys/{id}/chartable-questions.
export interface ChartableQuestionsResponse {
  questions: ChartableQuestion[];
  all_questions: ChartableQuestion[];
}

export interface SurveyChartBucket {
  key: string;
  label: string;
  denominator: number;
  // True when fewer than the anonymity floor actually answered in this
  // bucket - same protection as SurveyResponsesData.suppressed, applied to
  // the distribution chart types. counts/percentages are already zeroed out
  // server-side when this is true.
  suppressed: boolean;
  // Set on a by-instance bucket: its send instant, labelled client-side in
  // the viewer's timezone (see getCompactInstanceLabels).
  scheduled_datetime?: string;
}

export interface SurveyChartSeries {
  value_key: string;
  value_label: string;
  counts: number[];
  percentages: number[];
}

// Mirrors api.logic.custom_survey_analytics.ChartData.to_dict() - one
// chart's worth of computed data, nested in SurveyReportChart.data below.
export interface SurveyChartData {
  question: {
    uuid: string;
    type: string;
    label: string;
    values: { key: string; label: string }[];
    // The one statement a multi-statement LIKERT trend plots.
    statement_label?: string;
  };
  group_by: ChartGroupBy;
  period_granularity: ChartPeriodGranularity | null;
  is_trend: boolean;
  buckets: SurveyChartBucket[];
  series: SurveyChartSeries[];
}

// Mirrors compute_responses_from_dataset's return shape - the RESPONSES
// chart type's tabular alternative to a distribution chart.
export interface SurveyResponsesData {
  type: 'RESPONSES';
  question: {
    uuid: string;
    type: string;
    label: string;
    definition: Record<string, unknown>;
  };
  responses: { answer: Record<string, unknown> }[];
  response_count?: number;
  suppressed: boolean;
}

// One chart's computed data, however it was fetched (batched report
// chart-data, live preview, or draft preview) - null when the chart's
// question is missing/no-longer-chartable (see SurveyReportChart below).
export type SurveyChartResultData =
  | SurveyChartData
  | SurveyResponsesData
  | null;

// One entry in a report-level batched chart-data response - mirrors
// compute_report_chart_data's per-config dict. A chart whose question is
// missing/no-longer-chartable degrades to data: null + an error code,
// rather than failing the whole report.
export interface SurveyReportChart {
  config_id: number;
  name: string;
  width: ChartWidth;
  order: number;
  config: SurveyChartConfigBody;
  data: SurveyChartResultData;
  error: string | null;
}

// Returned by GET custom-survey/reports/{id}/chart-data.
export interface SurveyReportChartData {
  charts: SurveyReportChart[];
}

// One chart in the report builder's local draft state, saved/deleted/
// reordered together with the rest of the report - see
// CustomSurveyReportBuilder. `id` is the real chart_config id once
// persisted, or a client-only negative placeholder for a chart added but
// not yet saved. `data`/`error` come from the live preview endpoint below,
// recomputed whenever the draft's own identity (name/width/config) or the
// selected preview audience changes - not persisted server state.
export interface ChartDraft {
  id: number;
  name: string;
  width: ChartWidth;
  config: SurveyChartConfigBody;
  data: SurveyChartResultData;
  error: string | null;
}

// Body of POST custom-survey/reports/{id}/chart-data/preview.
export interface ChartDataPreviewRequest {
  charts: { config: SurveyChartConfigBody }[];
}

// One result of a chart-data/preview request, in the same order as the
// request's `charts` list - there's no id to key off of since those charts
// may not exist server-side yet.
export interface ChartDataPreviewResult {
  data: SurveyChartResultData;
  error: string | null;
}

export interface ChartDataPreviewResponse {
  results: ChartDataPreviewResult[];
}
